// Common connectors to remove from the keywords and searchQuery query
export const commonConnectors = [
  'and',
  'or',
  'the',
  'in',
  'on',
  'of',
  'for',
  'a',
  'an',
  'is',
  'to',
  'at',
  'with',
  'but',
  'by',
  'from',
  'as',
  'if',
  'that',
  'which',
  'who',
  'whom',
  'whose',
  'this',
  'these',
  'those',
  'then',
  'than',
  'because',
  'so',
  'although',
  'though',
  'while',
  'where',
  'when',
  'until',
  'unless',
  'since',
  'after',
  'before',
  'once',
  'whether',
  'however',
  'therefore',
  'thus',
  'hence',
  'nevertheless',
  'nonetheless',
  'moreover',
  'furthermore',
  'besides',
  'otherwise',
  'meanwhile',
  'whereas',
  'wherever',
  'whenever',
  'whereby',
  'whereupon',
  'wherein',
  'whereof',
  'whereas',
  'whereby',
  'whereupon',
  'wherein',
  'whereof',
  'whereas',
  'whereby',
  'whereupon',
  'wherein',
  'whereof',
]

type BaseFilter = {
  name: string
  label: string
  enabled: boolean
  visible: boolean
  value?: unknown
}

type BooleanFilterValue = boolean | undefined

type BooleanFilter = Omit<BaseFilter, 'value'> & {
  __typename: 'BooleanFilter'
  value?: BooleanFilterValue
}

type RangeFilterValue<T = string | number> = [T | undefined, T | undefined] | undefined

type RangeFilter<T = string | number> = Omit<BaseFilter, 'value'> & {
  __typename: 'RangeFilter'
  min?: T | undefined
  max?: T | undefined
  value?: RangeFilterValue
}

type ListFilterValue = string[] | undefined

type ListFilterOption = {
  label: string
  value: string
}

type ListFilter = Omit<BaseFilter, 'value'> & {
  __typename: 'ListFilter'
  options: ListFilterOption[]
  value?: ListFilterValue
}

// Filters presets

type PremiumFilterValue = boolean | undefined

/**
 * This type represents the premium filter
 */
type PremiumFilter = Omit<BaseFilter, 'value'> & {
  __typename: 'PremiumFilter'
  value?: PremiumFilterValue
}

type ResolutionFilterValue = 'all' | '4k' | '-4k'

/**
 * This type represents the resolution filter. It's an extension of the ListFilter type
 */
type ResolutionFilter = Omit<BaseFilter, 'value'> & {
  __typename: 'ResolutionFilter'
  value?: ResolutionFilterValue
}

const defaultResolutionFilterValue: ResolutionFilterValue = 'all'

type DurationFilterValue = RangeFilterValue<number>

/**
 * This type represents the duration filter. It's an extension of the RangeFilter type
 */
type DurationFilter = Omit<RangeFilter<number>, '__typename' | 'value'> & {
  __typename: 'DurationFilter'
  value?: DurationFilterValue
}

/**
 * This type represents the Sort filter
 */
type SortFilter = Omit<BaseFilter, 'value'> & {
  __typename: 'SortFilter'
  value?: SortFilterValue
}

type SortFilterValue = 'relevance' | 'new' | 'popular' | 'trending'

const defaultBrowseSortFilterValue: SortFilterValue = 'relevance'
const defaultSearchSortFilterValue: SortFilterValue = 'relevance'

const alternativeBrowseSortFilterValue: SortFilterValue = 'trending'
const alternativeSearchSortFilterValue: SortFilterValue = 'trending'
/**
 * This type represents the Broadness filter
 */
type BroadnessFilter = Omit<BaseFilter, 'value'> & {
  __typename: 'BroadnessFilter'
  value?: BroadnessFilterValue
}

type BroadnessFilterValue = number

const defaultBrowseBroadnessFilterValue = 0
const defaultSearchBroadnessFilterValue = 4

type Filter =
  | BooleanFilter
  | RangeFilter
  | ListFilter
  | PremiumFilter
  | ResolutionFilter
  | DurationFilter
  | SortFilter
  | BroadnessFilter

type FilterName = Filter['name']

type FilterValue =
  | BooleanFilterValue
  | RangeFilterValue
  | ListFilterValue
  | PremiumFilterValue
  | ResolutionFilterValue
  | DurationFilterValue
  | SortFilterValue
  | BroadnessFilterValue
  | undefined

/**
 * Convert a filter object to a query object
 */
const toQuery = (filters: Filter[]): Record<string, string> => {
  return filters.reduce(
    (acc, { __typename, name, value }) => {
      let queryValue = ''

      switch (__typename) {
        case 'BooleanFilter':
        case 'PremiumFilter':
          queryValue = value !== undefined ? (value ? 'true' : 'false') : ''
          break
        case 'RangeFilter':
        case 'DurationFilter':
          queryValue = value?.map((v) => (v === undefined ? '' : v)).join('-') ?? ''
          if (queryValue) {
            acc[name] = queryValue
          }
          break
        case 'ListFilter':
          queryValue = value?.join(',') ?? ''
          break
        case 'ResolutionFilter':
        case 'SortFilter':
        case 'BroadnessFilter':
          queryValue = value !== undefined ? `${value}` : ''
          break
        default:
          throw new Error(`Unknown filter type: ${__typename}`)
      }

      if (queryValue) {
        acc[name] = queryValue
      }

      return acc
    },
    {} as Record<string, string>
  )
}

/**
 * Get searchQuery params from a query string
 */
function getSearchParams(search: string): Record<string, string> {
  const params = new URLSearchParams(search)
  const result: Record<string, string> = {}

  params.forEach((value, key) => {
    result[key] = value
  })

  return result
}

/**
 * Build filters for content
 */
const getFiltersForContent = ({
  searchQuery,
  userIsPremium,
  query,
}: {
  mainCategorySlug?: string
  categorySlug?: string
  subCategorySlug?: string
  contentSlug?: string
  searchQuery?: string

  // Some filters depend on the user being premium
  userIsPremium?: boolean

  // The search query entered by the user
  query?: string
}): Filter[] => {
  const initialValues = getSearchParams(searchQuery ?? '')

  // Specific filters for main categories
  // Resolution filter was removed from the main category filters
  // const mainCategoryFilters: Filter[] = !isAudioMainCategory(mainCategorySlug)
  //   ? [
  //       {
  //         __typename: 'ResolutionFilter',
  //         name: 'resolution',
  //         label: 'Resolution',
  //         value: initialValues.resolution as ResolutionFilterValue,
  //       },
  //     ]
  //   : []

  // const mainCategoryFilters: Filter[] = []

  // Handle the special case when the user searches for "new" or "free"
  let sort = initialValues.sort as SortFilterValue
  let premium = initialValues.premium ? initialValues.premium === 'true' : undefined

  const sanitizedQuery = (query ?? '').trim().toLowerCase()

  const isNewQuery = sort === undefined && sanitizedQuery === 'new'
  const isFreeQuery = premium === undefined && sanitizedQuery === 'free'

  if (isNewQuery) {
    sort = 'new'
  }

  if (isFreeQuery) {
    premium = false
  }

  return [
    {
      __typename: 'SortFilter',
      name: 'sort',
      label: 'Order by',
      value: sort,
      visible: true,
      enabled: !isNewQuery,
    },
    {
      __typename: 'PremiumFilter',
      name: 'premium',
      label: 'Premium',
      value: premium,
      visible: !userIsPremium,
      enabled: !isFreeQuery,
    },
    /*{
      __typename: 'BroadnessFilter',
      name: 'broadness',
      label: 'Broadness',
      value: initialValues.broadness ? parseInt(initialValues.broadness) : undefined,
    },*/
  ]
}

/**
 * Get values from Next.js query params
 */
const getFiltersValues = (
  searchParams: URLSearchParams | Object
): {
  premium?: PremiumFilterValue
  sort?: SortFilterValue
  resolution?: ResolutionFilterValue
  broadness?: BroadnessFilterValue
} => {
  // Convert type if needed
  const urlSearchParams = !(searchParams instanceof URLSearchParams)
    ? new URLSearchParams(Object.entries(searchParams))
    : searchParams

  const premium: PremiumFilterValue | undefined = urlSearchParams.has('premium')
    ? urlSearchParams.get('premium') === 'true'
    : undefined
  const sort: SortFilterValue | undefined = (urlSearchParams.get('sort') as SortFilterValue) ?? undefined
  const resolution: ResolutionFilterValue | undefined =
    (urlSearchParams.get('resolution') as ResolutionFilterValue) ?? undefined
  const broadness: BroadnessFilterValue | undefined = urlSearchParams.has('broadness')
    ? parseInt(urlSearchParams.get('broadness') ?? '')
    : undefined

  return {
    premium,
    sort,
    resolution,
    broadness,
  }
}

export {
  toQuery,
  getFiltersForContent,
  getFiltersValues,
  defaultBrowseSortFilterValue,
  defaultSearchSortFilterValue,
  alternativeBrowseSortFilterValue,
  alternativeSearchSortFilterValue,
  defaultResolutionFilterValue,
  defaultBrowseBroadnessFilterValue,
  defaultSearchBroadnessFilterValue,
}
export type {
  Filter,
  FilterName,
  FilterValue,
  BooleanFilter,
  BooleanFilterValue,
  ListFilter,
  ListFilterOption,
  ListFilterValue,
  RangeFilter,
  RangeFilterValue,
  PremiumFilter,
  PremiumFilterValue,
  ResolutionFilter,
  ResolutionFilterValue,
  DurationFilter,
  DurationFilterValue,
  SortFilter,
  SortFilterValue,
  BroadnessFilter,
  BroadnessFilterValue,
}
